import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PlayCircle } from "lucide-react";
import { api, errMsg, fmtDate, fmtNum, rupiah } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Empty, Spinner } from "@/components/Brand";
import { PassBadge } from "@/components/ResultTable";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const PAY = { pending: "Menunggu verifikasi", approved: "Disetujui", rejected: "Ditolak" };

const Card = ({ label, value, testid }) => (
  <div className="card-premium p-5"><div className="text-xs font-bold uppercase tracking-widest text-slate-400">{label}</div><div className="mt-2 font-display text-3xl font-extrabold text-[#0A192F]" data-testid={testid}>{value}</div></div>
);

const History = ({ rows }) => (rows.length === 0 ? <Empty text="Belum ada riwayat tryout." testid="history-empty" /> : (
  <div className="overflow-x-auto rounded-xl border bg-white">
    <table className="w-full min-w-[640px] text-sm" data-testid="history-table">
      <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500"><tr><th className="px-4 py-3">Paket</th><th>Tanggal</th><th>Status</th><th>Total Skor</th><th>Kelulusan</th><th></th></tr></thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.id} className="border-t" data-testid={`history-row-${r.id}`}>
            <td className="px-4 py-3 font-semibold">{r.package_name}</td>
            <td>{fmtDate(r.finished_at || r.started_at)}</td>
            <td>{r.status === "finished" ? "Selesai" : "Berlangsung"}</td>
            <td className="font-mono font-bold">{r.hidden ? "Disembunyikan" : r.total != null ? `${fmtNum(r.total)}/${fmtNum(r.max_total)}` : "-"}</td>
            <td><PassBadge passed={r.passed} /></td>
            <td className="pr-4 text-right">{r.status === "finished"
              ? <Link to={`/hasil/${r.id}`} className="font-bold text-amber-700 hover:underline" data-testid={`history-view-${r.id}`}>Lihat hasil</Link>
              : <Link to={`/ujian/${r.id}`} className="font-bold text-emerald-700 hover:underline" data-testid={`history-resume-${r.id}`}>Lanjutkan</Link>}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
));

export default function Dashboard({ initialTab = "summary" }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [rows, setRows] = useState(null);
  const [pays, setPays] = useState([]);
  const [err, setErr] = useState("");
  const [tab, setTab] = useState(initialTab);

  useEffect(() => {
    setTab(initialTab);
  }, [initialTab]);
  useEffect(() => {
    api.get("/me/sessions").then((r) => setRows(r.data)).catch((e) => setErr(errMsg(e)));
    api.get("/me/payments").then((r) => setPays(r.data)).catch(() => {});
  }, []);

  if (user?.role === "admin" && initialTab === "summary") {
    return <div className="mx-auto max-w-3xl px-4 py-16 text-center"><p>Anda masuk sebagai admin.</p><button className="btn-gold mt-4 rounded-lg px-6 py-2.5" onClick={() => nav("/admin")} data-testid="goto-admin-btn">Buka Dashboard Admin</button></div>;
  }
  if (err) return <div className="mx-auto max-w-4xl px-4 py-16"><Empty text={err} /></div>;
  if (!rows) return <Spinner />;
  const done = rows.filter((r) => r.status === "finished");
  const scored = done.filter((r) => r.total != null);
  const ongoing = rows.filter((r) => r.status === "in_progress");
  const passFlags = done.filter((r) => r.passed != null);

  return (
    <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6">
      <p className="text-xs font-bold uppercase tracking-[0.25em] text-amber-600">Dashboard Peserta</p>
      <h1 className="mt-2 text-3xl font-extrabold text-[#0A192F]" data-testid="dashboard-greeting">Halo, {user.name}</h1>
      <Tabs value={tab} onValueChange={setTab} className="mt-8">
        <TabsList>
          <TabsTrigger value="summary" data-testid="tab-summary">Ringkasan</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">Riwayat</TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payments">Pembayaran</TabsTrigger>
        </TabsList>
        <TabsContent value="summary" className="space-y-8 pt-4">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Card label="Tryout selesai" value={done.length} testid="my-finished-count" />
            <Card label="Rata-rata total skor" value={scored.length ? fmtNum(scored.reduce((a, r) => a + r.total, 0) / scored.length) : "-"} testid="my-average" />
            <Card label="Lulus" value={passFlags.length ? `${passFlags.filter((r) => r.passed).length}/${passFlags.length}` : "-"} testid="my-pass-count" />
            <Card label="Sedang berlangsung" value={ongoing.length} testid="my-ongoing-count" />
          </div>
          {ongoing.map((r) => (
            <div key={r.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-emerald-300 bg-emerald-50 p-4">
              <span className="font-semibold">Ujian <b>{r.package_name}</b> masih berlangsung.</span>
              <button onClick={() => nav(`/ujian/${r.id}`)} className="btn-gold flex items-center gap-2 rounded-lg px-4 py-2 text-sm" data-testid={`resume-${r.id}`}><PlayCircle className="h-4 w-4" />Lanjutkan</button>
            </div>
          ))}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-display text-lg font-bold text-[#0A192F]">Riwayat terbaru</h2>
            <button onClick={() => nav("/tryout")} className="btn-gold rounded-lg px-5 py-2 text-sm" data-testid="dashboard-find-tryout">Cari Tryout</button>
          </div>
          <History rows={rows.slice(0, 5)} />
        </TabsContent>
        <TabsContent value="history" className="pt-4"><History rows={rows} /></TabsContent>
        <TabsContent value="payments" className="pt-4">
          {pays.length === 0 ? <Empty text="Belum ada pembayaran." /> : (
            <div className="overflow-x-auto rounded-xl border bg-white">
              <table className="w-full min-w-[520px] text-sm" data-testid="my-payments-table">
                <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500"><tr><th className="px-4 py-3">Paket</th><th>Metode</th><th>Jumlah</th><th>Tanggal</th><th>Status</th><th>Catatan admin</th></tr></thead>
                <tbody>{pays.map((p) => <tr key={p.id} className="border-t"><td className="px-4 py-3 font-semibold">{p.package_name}</td><td>{p.method === "midtrans" ? "Online (Midtrans)" : "Transfer manual"}</td><td>{rupiah(p.amount)}</td><td>{fmtDate(p.created_at)}</td><td>{PAY[p.status]}</td><td>{p.admin_note || "-"}</td></tr>)}</tbody>
              </table>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
